/**
 * Thin client for the lore HTTP API. Mirrors the shape of sessionApi.ts —
 * Cognito ID token as bearer, JSON in / JSON out, errors thrown as plain
 * Error instances with the server's `error` field.
 *
 * The lore API is *caller-scoped* on the server side. The same endpoints
 * return different content depending on which Cognito sub the request comes
 * from, and (for the DM) the optional `as` query parameter switches into
 * preview-as-player mode. The client doesn't model "DM vs player" — it just
 * fetches; the server enforces visibility.
 */
import { getIdToken } from './auth';

const BaseUrl = import.meta.env.VITE_API_URL as string;

/**
 * The four lore types we care about. Mirrors the C# `LoreEntryType` enum.
 * Canvas notes are deferred to a placeholder tile in v1 — the Lore page
 * renders them as a disabled card linking to /sheet.
 */
export type LoreEntryType = 'markdown' | 'canvas';

export interface LoreManifestEntry {
  slug: string;
  relativePath: string;
  title: string;
  folder: string;
  type: LoreEntryType;
  summary?: string;
  tags?: string[];
}

export interface LoreManifest {
  entries: LoreManifestEntry[];
  /** Non-null when players.json is missing or malformed. DM-only diagnostic. */
  warning?: string;
}

export interface LoreLinkRef {
  rawTarget: string;
  displayText: string;
  resolvedSlug?: string;
  /**
   * False for both "target doesn't exist" AND "target exists but caller isn't
   * authorised". The two cases MUST stay indistinguishable to prevent players
   * from probing for hidden notes by inspecting which links are clickable.
   */
  visible: boolean;
}

/**
 * One image embed (`![[name]]`) the server has resolved against the caller's
 * visible-image set. As with link refs, "doesn't exist" and "exists but
 * hidden" both collapse to `visible: false` with no slug — the client must
 * not be able to distinguish them.
 */
export interface LoreImageRef {
  rawTarget: string;
  slug?: string;
  visible: boolean;
}

export interface LoreNote {
  slug: string;
  type: LoreEntryType;
  /** Markdown body, frontmatter already stripped and gated blocks removed. */
  content: string;
  frontmatter: {
    players?: { all?: boolean; explicitNone?: boolean; allowed?: string[] };
    reveal?: string;
    summary?: string;
    tags?: string[];
  };
  links: LoreLinkRef[];
  images: LoreImageRef[];
  etag: string;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  if (!BaseUrl) {
    throw new Error('VITE_API_URL is not configured');
  }
  const token = await getIdToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const resp = await fetch(`${BaseUrl}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (resp.status === 204) {
    return undefined as T;
  }

  const text = await resp.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!resp.ok) {
    const msg =
      payload && typeof payload === 'object' && payload && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Lore request failed (${resp.status})`;
    throw new Error(msg);
  }

  return payload as T;
}

/**
 * Fetches the caller-scoped manifest. Pass `refresh=true` to bust the
 * server's 60s warm cache — the DM uses this after editing in Obsidian.
 *
 * `as` is the DM-only preview-as-player parameter. The server silently
 * ignores it for non-DM callers, so the client can pass it freely; the worst
 * a player can do is get back their normal manifest.
 */
export function getManifest(opts?: {
  refresh?: boolean;
  as?: string;
}): Promise<LoreManifest> {
  const params = new URLSearchParams();
  if (opts?.refresh) params.set('refresh', '1');
  if (opts?.as) params.set('as', opts.as);
  const qs = params.toString();
  return request('GET', `/lore/manifest${qs ? `?${qs}` : ''}`);
}

/**
 * Fetches a single note by slug. Slug must be the value from the manifest
 * (it's already URL-encoded per segment); the client just appends it.
 */
export function getNote(slug: string, opts?: { as?: string }): Promise<LoreNote> {
  const params = new URLSearchParams();
  if (opts?.as) params.set('as', opts.as);
  const qs = params.toString();
  return request('GET', `/lore/note/${slug}${qs ? `?${qs}` : ''}`);
}

/**
 * Fetches an image from the lore image endpoint and returns it as a blob URL
 * the caller is responsible for revoking. We use a fetch-then-blob dance
 * because <img src="..."> can't carry the Authorization header — every image
 * the lore page renders flows through this helper, gets cached as an object
 * URL, and is revoked on note unload.
 *
 * The server enforces visibility on this endpoint independently of whether
 * the client knew the image was visible, so this is safe to call with any
 * slug. Hidden/missing images come back as 404 and the caller falls through
 * to the placeholder.
 */
export async function fetchImageBlob(slug: string, opts?: { as?: string }): Promise<string> {
  if (!BaseUrl) {
    throw new Error('VITE_API_URL is not configured');
  }
  const token = await getIdToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  const params = new URLSearchParams();
  if (opts?.as) params.set('as', opts.as);
  const qs = params.toString();
  const resp = await fetch(`${BaseUrl}/lore/image/${slug}${qs ? `?${qs}` : ''}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    throw new Error(`Image fetch failed (${resp.status})`);
  }
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}

// ---------------------------------------------------------------------------
// DM write API. Every endpoint below is DM-only on the server side. Players
// hitting them get a 403 (or 404 for non-existent slugs); the client doesn't
// pre-gate, so the calling component must hide the editor for non-DM users.
// ---------------------------------------------------------------------------

/** Saved draft from `GET /lore/draft/{slug+}`. */
export interface LoreDraft {
  slug: string;
  content: string;
  baseEtag: string;
  updatedAt: string;
}

/** Lightweight draft summary from `GET /lore/drafts`. */
export interface LoreDraftSummary {
  slug: string;
  baseEtag: string;
  updatedAt: string;
}

export interface LoreDraftListResponse {
  entries: LoreDraftSummary[];
}

/**
 * 412 conflict body returned by `putNote` and `publishDraft`. The client
 * surfaces this via a 3-way merge UI rather than overwriting the vault.
 */
export interface LoreConflict {
  slug: string;
  currentEtag: string;
  currentBody: string;
}

export class LoreConflictError extends Error {
  constructor(public readonly conflict: LoreConflict) {
    super('Vault was modified since this draft was loaded');
    this.name = 'LoreConflictError';
  }
}

export interface NoteWriteResponse {
  slug: string;
  etag: string;
}

export interface DraftSaveResponse {
  slug: string;
  savedAt: string;
}

/**
 * Internal request helper that knows about 412 conflicts. Used by
 * `putNote` and `publishDraft`. We can't reuse the top-level `request` because
 * it treats anything non-2xx as an Error and we want to keep the conflict
 * payload structured.
 */
async function writeRequest<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  if (!BaseUrl) {
    throw new Error('VITE_API_URL is not configured');
  }
  const token = await getIdToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const resp = await fetch(`${BaseUrl}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (resp.status === 204) {
    return undefined as T;
  }

  const text = await resp.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (resp.status === 412) {
    throw new LoreConflictError(payload as LoreConflict);
  }

  if (!resp.ok) {
    const msg =
      payload && typeof payload === 'object' && payload && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Lore write failed (${resp.status})`;
    throw new Error(msg);
  }

  return payload as T;
}

/**
 * Raw source for the editor (DM only). Returns the WHOLE markdown including
 * frontmatter and any `%%` blocks — `getNote` returns a stripped/gated body
 * which is wrong for editing. The etag is the current S3 etag and must be
 * passed back to `putNote` / `putDraft` for conditional writes.
 */
export interface LoreSource {
  slug: string;
  content: string;
  etag: string;
}

export function getNoteSource(slug: string): Promise<LoreSource> {
  return request('GET', `/lore/source/${slug}`);
}

/** Whole-note save (DM only). Throws `LoreConflictError` on 412. */
export function putNote(
  slug: string,
  content: string,
  baseEtag: string,
): Promise<NoteWriteResponse> {
  return writeRequest('PUT', `/lore/note/${slug}`, { content, baseEtag });
}

/** Save a draft to DynamoDB. Called on debounced keystrokes by the editor. */
export function putDraft(
  slug: string,
  content: string,
  baseEtag: string,
): Promise<DraftSaveResponse> {
  return writeRequest('PUT', `/lore/draft/${slug}`, { content, baseEtag });
}

/** Returns null if there is no draft for this slug (server replies 404). */
export async function getDraft(slug: string): Promise<LoreDraft | null> {
  try {
    return await request<LoreDraft>('GET', `/lore/draft/${slug}`);
  } catch (err) {
    if (err instanceof Error && /\(404\)/.test(err.message)) return null;
    if (err instanceof Error && /No draft/i.test(err.message)) return null;
    throw err;
  }
}

export function deleteDraft(slug: string): Promise<void> {
  return writeRequest('DELETE', `/lore/draft/${slug}`);
}

export function listDrafts(): Promise<LoreDraftListResponse> {
  return request('GET', `/lore/drafts`);
}

/**
 * Publish a draft: server reads the draft from DDB, conditional-PUTs it to
 * S3 with the draft's stored baseEtag, deletes the draft on success. Throws
 * `LoreConflictError` on 412 — the editor surfaces the conflict body.
 */
export function publishDraft(slug: string): Promise<NoteWriteResponse> {
  return writeRequest('POST', `/lore/publish/${slug}`, {});
}

// ---------------------------------------------------------------------------
// Stateless transform endpoint. Used by the section toolbar (wrap a selection
// in a `%% players: ... %%` or `%% reveal: ... %%` block) and the frontmatter
// form (set/clear individual frontmatter keys without disturbing comments,
// ordering, quoting, or unknown keys). Both go through one endpoint so the
// line-based YAML editor can stay in C# where it has tests.
// ---------------------------------------------------------------------------

export interface PlayersPatch {
  all?: boolean;
  none?: boolean;
  allowed?: string[];
}

export interface FrontmatterPatch {
  setPlayers?: boolean;
  players?: PlayersPatch | null;
  setReveal?: boolean;
  reveal?: string | null;       // ISO yyyy-MM-dd, or null to clear
  setSummary?: boolean;
  summary?: string | null;
  setTags?: boolean;
  tags?: string[] | null;
}

export type SectionWrapKind = 'players' | 'reveal';

export interface SectionWrap {
  wrapKind: SectionWrapKind;
  /** 0-based inclusive line index in the body (no frontmatter). */
  startLine: number;
  /** 0-based inclusive line index in the body (no frontmatter). */
  endLine: number;
  /** For `players` wraps: comma-separated player folder names, or undefined for "all". */
  playersList?: string;
  /** For `reveal` wraps: ISO yyyy-MM-dd. */
  revealDate?: string;
}

export interface TransformResponse {
  body: string;
}

/**
 * Apply a frontmatter patch. The body MUST be the WHOLE markdown including
 * the frontmatter block — the line-based writer needs the existing YAML to
 * preserve comments, ordering, quoting, and unknown keys.
 */
export function transformFrontmatter(
  body: string,
  patch: FrontmatterPatch,
): Promise<TransformResponse> {
  return request('POST', '/lore/transform', {
    kind: 'frontmatter',
    body,
    frontmatter: patch,
  });
}

/**
 * Wrap a body region in a `%%` block. The body MUST be the markdown WITHOUT
 * the frontmatter (the wrapper operates in body-local line numbers).
 */
export function transformSection(
  body: string,
  section: SectionWrap,
): Promise<TransformResponse> {
  return request('POST', '/lore/transform', {
    kind: 'section',
    body,
    section,
  });
}

// ---------------------------------------------------------------------------
// Player notes — `/lore/my-notes`. Every authenticated caller can use these;
// the server pins each call to the caller's own `player-notes/{campaign}/
// {folder}/` prefix using the existing players.json mapping. There is no
// separate DM endpoint — the DM hits the same routes with `?as={folder}`
// (the existing preview-as-player query param) to act as a player.
//
// Slug semantics for these routes: the per-segment URL-encoded path BELOW
// the player's own prefix. The player folder and campaign id are NEVER part
// of the slug — the server appends them from the authenticated identity.
// e.g. a note at `Combats/Wally vs goblins.md` has slug
// `Combats/Wally%20vs%20goblins.md`.
// ---------------------------------------------------------------------------

export interface MyNoteEntry {
  slug: string;
  etag: string;
  size: number;
  updatedAt: string;
}

export interface MyNoteListResponse {
  player?: string;
  entries: MyNoteEntry[];
}

export interface MyNote {
  slug: string;
  player: string;
  content: string;
  etag: string;
}

export function listMyNotes(): Promise<MyNoteListResponse> {
  return request('GET', '/lore/my-notes');
}

export function getMyNote(slug: string): Promise<MyNote> {
  return request('GET', `/lore/my-notes/${slug}`);
}

/**
 * Create-or-update a player note. Pass `baseEtag` to update an existing
 * note (server returns 412 → `LoreConflictError` if the vault has moved
 * underneath you). Omit `baseEtag` to create — the server existence-probes
 * and refuses with 409 if a note already lives at that slug, so two players
 * can never silently overwrite each other.
 */
export function putMyNote(
  slug: string,
  content: string,
  baseEtag?: string,
): Promise<NoteWriteResponse> {
  return writeRequest('PUT', `/lore/my-notes/${slug}`, {
    content,
    ...(baseEtag ? { baseEtag } : {}),
  });
}

export function deleteMyNote(slug: string): Promise<void> {
  return writeRequest('DELETE', `/lore/my-notes/${slug}`);
}
